import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { GateScreen, type GateState } from '../components/states/Gate.tsx';
import { faultOf } from '../components/states/fault.ts';
import { cognitoConfig } from './config.ts';
/* The gate renders before any Surface does, and it runs the same 1px bar. */
import '../components/states/motion.css';

/* ==========================================================================
   IAP-like gate for the Cognito deployment (PRD-0006 D9): an unauthenticated
   visitor is sent straight into the OIDC code flow, and Cognito's hosted UI is
   told to go directly to the Google IdP so there is no intermediate account
   chooser. Rendered only when auth is enabled (see main.tsx); local dev never
   reaches it.

   What the gate renders while that happens is a state, and it is built out of
   the same vocabulary as every other state in the console — see
   components/states/Gate.tsx. Two things it does that the previous version
   did not:

     · It distinguishes the four situations. "Redirecting", "exchanging the
       code", "the provider refused" and "the session ended" are four
       different facts with three different recoveries, and one centred
       sentence covered all of them.
     · It stops a redirect loop. A provider that hands us back with no session
       and no error — an expired cookie, a blocked third-party context, a
       misconfigured callback — used to be answered by redirecting again,
       forever, with the words "Redirecting to sign-in…" on screen the whole
       time. After two attempts the gate stops and says so, with the
       authority, client and redirect URI it was using.
   ========================================================================== */

const ATTEMPT_KEY = 'f3.auth.redirects';
const MAX_REDIRECTS = 2;

export function AuthGate({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  const cfg = cognitoConfig();
  const [since] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());
  const looping = useRef(false);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(t);
  }, []);

  const start = useCallback(() => {
    sessionStorage.setItem(ATTEMPT_KEY, String(attempts() + 1));
    void auth.signinRedirect();
  }, [auth]);

  const retry = useCallback(() => {
    sessionStorage.removeItem(ATTEMPT_KEY);
    void auth.signinRedirect();
  }, [auth]);

  useEffect(() => {
    if (auth.isLoading || auth.isAuthenticated || auth.error) return;
    if (attempts() >= MAX_REDIRECTS) {
      looping.current = true;
      return;
    }
    start();
  }, [auth.isLoading, auth.isAuthenticated, auth.error, start]);

  useEffect(() => {
    if (auth.isAuthenticated) sessionStorage.removeItem(ATTEMPT_KEY);
  }, [auth.isAuthenticated]);

  if (auth.isAuthenticated) return <>{children}</>;

  const exchanging = new URLSearchParams(window.location.search).has('code');
  const state: GateState = auth.error
    ? 'failed'
    : looping.current
      ? 'failed'
      : exchanging
        ? 'exchanging'
        : 'redirecting';

  /* THE PROVIDER'S FAILURE, IN THE CONSOLE'S WORDS.

     `auth.error.message` is whatever oidc-client threw, and the gate renders
     it twice — in the strip's detail and against `Provider said` in the
     ladder. For the class this gate actually hits hardest, the browser could
     not reach the authority at all, and that string is `Failed to fetch`:
     the browser's account of its own plumbing, printed under the words
     "Provider said", where it is not even true — the provider said nothing.

     `faultOf` is the same classifier the query client and every panel fault
     line use, so the gate classifies rather than quotes. A transport failure
     becomes the house phrase for this surface (the authority, not the API,
     is what did not answer); a provider's OWN message — `invalid_grant`, a
     mismatched redirect_uri — is passed through untouched, because that is
     the most useful string on the screen and the whole reason the row is
     there. */
  const authFault = auth.error ? faultOf(auth.error) : null;
  const reason = auth.error
    ? authFault !== null && (authFault.class === 'link' || authFault.class === 'timeout')
      ? `no reply from ${cfg === null ? 'the provider' : hostOf(cfg.authority)}`
      : auth.error.message
    : looping.current
      ? `${cfg === null ? 'the provider' : hostOf(cfg.authority)} returned without a session, twice`
      : null;

  return (
    <GateScreen
      state={state}
      authority={cfg?.authority ?? null}
      clientId={cfg?.clientId ?? null}
      redirectUri={`${window.location.origin}/auth/callback`}
      reason={reason}
      sinceMs={now - since}
      onRetry={retry}
    />
  );
}

function attempts(): number {
  const raw = Number(sessionStorage.getItem(ATTEMPT_KEY) ?? '0');
  return Number.isFinite(raw) ? raw : 0;
}

function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}
